import React, { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { MenuLayout } from '@templates/Menu/MenuLayout.ui';
import { GetServerSideProps } from 'next';
import { QueryClient } from 'react-query';
import { dehydrate } from 'react-query/hydration';
import { EModalTypes, EModalSubTypes } from '@olo-web/types/enums';
import { createMerchantPath } from '@domain/merchants/hooks/useMerchantPath';
import { getMerchantThemeSSR } from '@domain/merchants/functions/getMerchantThemeSSR';
import { useDefaultLocation, getPreferredLocation } from '@olo-web/utils/common/hooks';
import {
  getPreferredLocationCookieName,
  isFeatureEnabledForMerchant,
  slug,
} from '@olo-web/utils/common/functions';
import { isOrderingAvailable } from '@domain/merchants/functions';
import { ECookies, EFirestoreCollections } from '@olo-web/types/enums';
import nookies from 'nookies';
import { useFeatureFlags } from '@olo-web/domain/featureFlags/queries';
import { getLDUserValuesFromCtx } from '@olo-web/utils';
import {
  getFeatureFlags,
  ldCookieOptions,
  scopeFlagsForClientResponse,
} from '@olo-web/services/launchDarkly';
import { v4 as uuid } from 'uuid';
import { useCustomerDispatch, useModalDispatch } from '@olo-web/client-state';
import {
  configureMerchantData,
  configureMerchantGroupData,
} from '@domain/merchants/functions/configureMerchantData';
import { getMerchant } from '@olo-web/services/emagine/getMerchant';
import { getMerchantGroup } from '@olo-web/services/emagine/getMerchantGroup';

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  try {
    const { merchantId, ip } = getLDUserValuesFromCtx(ctx);
    const {
      params: { locationSlug },
      res: { statusCode },
    } = ctx;

    if (!merchantId) return { notFound: true };

    const cookie = nookies.get(ctx);
    const userKey = cookie[ECookies.ldUserKey] || uuid();
    if (!cookie[ECookies.ldUserKey]) nookies.set(ctx, ECookies.ldUserKey, userKey, ldCookieOptions);

    const [featureFlags, merchantRes, merchantGroupRes] = await Promise.all([
      getFeatureFlags(userKey, merchantId as string, ip as string),
      getMerchant(merchantId),
      getMerchantGroup(merchantId as string),
    ]);
    const merchant = configureMerchantData(
      merchantRes?.data,
      featureFlags?.disallowDoorDashDriveOrders
    );
    const merchantGroup = configureMerchantGroupData(
      merchantGroupRes?.data,
      featureFlags?.disallowDoorDashDriveOrders
    );

    if (statusCode === 500) {
      return {
        redirect: {
          destination: `${createMerchantPath({
            merchantId: merchantId as string,
            merchant,
            groupId: merchantGroup?.id,
          })}/ordering-unavailable`,
          permanent: false,
        },
      };
    }

    if (!merchant?.isOrderingAvailable) {
      const firstOpenSibling = merchantGroup?.merchants?.find(isOrderingAvailable);

      if (firstOpenSibling) {
        return {
          redirect: {
            destination: `${createMerchantPath({
              merchantId: firstOpenSibling.merchantId,
              merchant: firstOpenSibling,
              groupId: merchantGroup?.id,
            })}/welcome`,
            permanent: false,
          },
        };
      }

      return {
        redirect: {
          destination: `${createMerchantPath({
            merchantId: merchantId as string,
            merchant,
            groupId: merchantGroup?.id,
          })}/ordering-unavailable`,
          permanent: false,
        },
      };
    }

    const preferredLocation = getPreferredLocation(
      cookie?.[getPreferredLocationCookieName(merchantGroup?.id)],
      merchantGroup
    );

    if (preferredLocation) {
      return {
        redirect: {
          destination: createMerchantPath({
            merchantId: preferredLocation?.merchantId,
            merchant: preferredLocation,
            groupId: merchantGroup?.id,
          }),
          permanent: false,
        },
      };
    }

    if (locationSlug === '-') {
      return {
        redirect: {
          destination: `${createMerchantPath({
            merchantId: merchantId as string,
            merchant,
            groupId: merchantGroup?.id,
          })}/welcome`,
          permanent: false,
        },
      };
    }

    const queryClient = new QueryClient();

    const promises = [
      queryClient.prefetchQuery(
        ['merchant', merchantId],
        // @ts-ignore
        () => merchant
      ),
      queryClient.prefetchQuery(
        ['merchant-group', merchantId],
        // @ts-ignore
        () => merchantGroup
      ),
      queryClient.prefetchQuery(
        ['feature-flags', merchantId, userKey],
        //@ts-ignore
        () => scopeFlagsForClientResponse(featureFlags, merchantId as string)
      ),
    ];

    if (!featureFlags?.hideMerchantTheming) {
      promises.push(
        queryClient.prefetchQuery(
          [EFirestoreCollections.MERCHANT_THEME, merchantId],
          // @ts-ignore
          () => getMerchantThemeSSR(merchantId)
        )
      );
    }

    await Promise.all(promises);

    return {
      props: {
        dehydratedState: dehydrate(queryClient),
      },
      // every 6 hours (in secs)
    };
  } catch (e) {
    if (e?.response?.status === 404) {
      return {
        notFound: true,
      };
    }

    throw e;
  }
};

const Welcome = () => {
  const { defaultLocation, multipleLocations } = useDefaultLocation();
  const [hasWelcomed, setHasWelcomed] = useState(false);
  const router = useRouter();
  const customerDispatch = useCustomerDispatch();
  const { data: featureFlags } = useFeatureFlags();
  const cookie = nookies.get();
  const modalDispatch = useModalDispatch();
  const { locationSlug, merchantSlug, merchantId } = router.query;
  const url = `/${locationSlug}/${merchantSlug}/${merchantId}`;

  useEffect(() => {
    if (!featureFlags || hasWelcomed) return; // prevents modal open action from being fired twice so that user can't go back to the previous modal which has the incorrect feature flag config

    if (defaultLocation?.merchantId) {
      const url = `/${slug(defaultLocation.orgName)}-${defaultLocation.orgId}/${slug(
        defaultLocation.city
      )}/${defaultLocation.merchantId}`;
      router.push(url, url, { shallow: true });
    } else if (multipleLocations) {
      const requireLocationSelection =
        featureFlags?.requireLocationSelection?.requireAll === true ||
        featureFlags?.requireLocationSelection?.merchantIds?.includes(merchantId as string);
      const showListView = isFeatureEnabledForMerchant({
        merchantId: merchantId as string,
        featureEnabled: featureFlags?.showListViewInLocationSelector?.on,
        allowList: featureFlags?.showListViewInLocationSelector?.allow,
        denyList: featureFlags?.showListViewInLocationSelector?.deny,
      });
      modalDispatch({
        type: 'OPEN_MODAL',
        payload: {
          modalKey: EModalTypes.MERCHANT_LOCATION,
          subType: showListView ? EModalSubTypes.LOCATION.LIST : EModalSubTypes.LOCATION.MAP,
          modalContext: {
            onClose: () => !requireLocationSelection && router.push(url, url, { shallow: true }),
            canClose: !requireLocationSelection,
          },
          modalProps: {
            closeOnOverlayClick: !requireLocationSelection,
            closeOnEsc: !requireLocationSelection,
          },
        },
      });
    } else {
      router.push(url, url, { shallow: true });
    }
    setHasWelcomed(true);
  }, [
    defaultLocation?.city,
    defaultLocation?.merchantId,
    defaultLocation?.orgName,
    defaultLocation?.orgId,
    featureFlags?.requireLocationSelection,
    hasWelcomed,
    merchantId,
    router,
    customerDispatch,
    cookie,
    modalDispatch,
    multipleLocations,
    url,
    featureFlags,
  ]);

  return <MenuLayout />;
};

export default Welcome;
